// ─────────────────────────────────────────────────────────────────────────────
// Operational-intent display vocabulary.
//
// The backend broadcasts each drone's airspace-claim state on every telemetry
// frame — including on the read-only entries for drones this operator does not
// own (see backend/services/intentService.js). That is the point of conformance
// monitoring: "that aircraft is not where it declared it would be" is the
// neighbours' problem, not just the owner's.
//
// This module is the one place that decides what each state LOOKS like, so the
// 2D map, the 3D map and the fleet panel cannot drift into three different
// colours for the same condition — which is precisely how an operator learns to
// distrust the display.
//
// States come from ASTM F3548 (models/OperationalIntent.js).
// ─────────────────────────────────────────────────────────────────────────────

export const INTENT_DISPLAY = {
  // Airspace claimed, not yet airborne. Deliberately understated — a reserved
  // corridor is normal, and colouring it would train operators to ignore colour.
  Accepted: { label: 'RESERVED', short: 'RSV', color: '#8b93a7' },

  // Airborne and following the declared route. The nominal case: no colour
  // override, so the marker keeps its usual owned/shared styling.
  Activated: { label: 'ON PLAN', short: 'OK', color: null },

  // Outside the declared corridor, still recoverable. Amber matches the
  // landing-phase corridor colour already used on the 3D map.
  Nonconforming: { label: 'OFF CORRIDOR', short: 'DEV', color: '#fbbf24' },

  // Off-nominal: C2 loss, critical battery, operator abort. The drone is flying
  // a route nobody declared, and where it actually is has become less certain —
  // so it reads as the loudest thing on the map.
  Contingent: { label: 'CONTINGENCY', short: 'CTG', color: '#ef4444' },
};

export function intentDisplay(state) {
  return INTENT_DISPLAY[state] || null;
}

// True when the drone is not where it said it would be. This is the predicate
// worth branching on — the specific state matters for the label, not for
// whether the operator needs to look.
export function isOffNominal(state) {
  return state === 'Nonconforming' || state === 'Contingent';
}

// Marker colour for a drone, or null to keep the caller's existing styling.
// Only off-nominal states override: if every state repainted the marker, the
// repaint would stop carrying information.
export function intentMarkerColor(state) {
  const d = intentDisplay(state);
  return d && isOffNominal(state) ? d.color : null;
}
